import { lazy, useContext } from 'react';
import { Navigate, Outlet, Route } from 'react-router-dom';
import AuthCallback from '../components/routing/AuthCallbackIanua';
import ConnectCallback from '../components/routing/ConnectCallbackIanua';
import DisconnectCallback from '../components/routing/DisconnectCallbackIanua';
import EditCallback from '../components/routing/EditCallbackIanua';
import ProtectedRoute from '../components/routing/ProtectedRoute';
import RequireAuth from '../components/routing/RequireAuthIanua';
import IdentityContext from '../contexts/IdentityContext';
import { useRoutes } from '../hooks/useRoutes';
import useStudioGroup from '../hooks/useStudioGroup';
import PageLevelLayout from '../layout/PageLevelLayout';
import GroupInvite from '../pages/group-invite';
import InfoHelp from '../pages/help';
import LaunchMaterial from '../pages/launch-material';
import LinkedMaterialList from '../pages/linked-material-list';
import Login from '../pages/login';
import { SidebarVisibilityContextProvider } from '../pages/material/_contexts/SidebarVisibilityContext';
import StudentAssignments, { StudentAssignmentTabContent } from '../pages/material/assignments/student';
import TeacherAssignments, { TeacherAssignmentTabs } from '../pages/material/assignments/teacher';
import CategoricalMaterialOverview from '../pages/material/categorical';
import CategoricalMaterialPage from '../pages/material/categorical/CategoricalMaterialPage';
import CategoricalMaterialBrowseMode from '../pages/material/categorical/browse-mode';
import { CategoricalContentTypeContextProvider } from '../pages/material/categorical/context/CategoricalContentTypeContext';
import { QueryMaterialContextProvider } from '../pages/material/categorical/context/QueryMaterialContext';
import Results from '../pages/material/results';
import ExerciseTab from '../pages/material/results/components/ExerciseTab';
import StudentTab from '../pages/material/results/components/per-student/StudentTab';
import Search from '../pages/material/search';
import SubModuleSelector from '../pages/material/sub-module-selector';
import Toc from '../pages/material/toc';
import VirtualExerciseModuleOverview from '../pages/material/virtual-exercise-module';
import VirtualExerciseModulePage from '../pages/material/virtual-exercise-module/VirtualExerciseModulePage';
import VirtualExerciseModuleCategoryDetail from '../pages/material/virtual-exercise-module/components/VirtualExerciseModuleCategoryDetail';
import MyGroups, { GroupsTabContent } from '../pages/my-groups';
import MyMethods from '../pages/my-methods';
import CataloguePage from '../pages/my-methods/Catalogue';
import Dashboard from '../pages/my-methods/Dashboard';
import MyProfile from '../pages/my-profile';
import MyDetails from '../pages/my-profile/components/MyDetails';
import MyLicenses from '../pages/my-profile/my-licenses';
import MyTeachers, { MyTeacherTabContent } from '../pages/my-teachers';
import Conditions from '../pages/policy-and-conditions/conditions';
import Privacy from '../pages/policy-and-conditions/privacy';
import LaunchQRCode from '../pages/qr-codes';
import { withSuspense } from '../utils/suspense';
import StandaloneWordlist from '../pages/standalone-wordlist';

const NotFound = lazy(() => import('../components/errors/NotFound'));
const MaterialDataProvider = lazy(() => import('../pages/material/_contexts/MaterialDataProvider'));
const AppLayoutIanua = lazy(() => import('../AppLayout.ianua'));

function useRouter() {
  const { identity } = useContext(IdentityContext);
  const { isTeacherAlike } = identity;
  const { getRoute } = useRoutes();
  const studioGroup = useStudioGroup();

  const isCodeBased = studioGroup === 'BAO' || studioGroup === 'VO';
  const showGroupsAndProfile = isTeacherAlike || !isCodeBased;
  const isCodeBasedStudent = isCodeBased && !isTeacherAlike;

  return (
    <Route path="/">
      <Route index element={<Navigate to={getRoute('MY_METHODS')} replace />} />
      <Route element={withSuspense(AppLayoutIanua)}>
        <Route path={getRoute('LOGIN')} element={<Login />} />
        {studioGroup === 'VO' && <Route path={getRoute('LOGIN_VO_TEACHER')} element={<Login />} />}
        <Route path={getRoute('LOGIN_CALLBACK')} element={<AuthCallback />} />
        <Route path={getRoute('CONNECT_CALLBACK')} element={<ConnectCallback />} />
        <Route path={getRoute('DISCONNECT_CALLBACK')} element={<DisconnectCallback />} />
        <Route path={getRoute('EDIT_CALLBACK')} element={<EditCallback />} />
        <Route path={getRoute('HELP')} element={<InfoHelp />} />
        <Route path={getRoute('PRIVACY_POLICY')} element={<Privacy />} />
        <Route path={getRoute('TERMS_AND_CONDITIONS')} element={<Conditions />} />

        <Route element={<RequireAuth />}>
          <Route path={getRoute('INVITE')} element={<GroupInvite />} />

          <Route path={getRoute('MY_METHODS')} element={<MyMethods />}>
            <Route index element={<Navigate to={getRoute('MY_METHODS.DASHBOARD')} replace />} />
            <Route path={getRoute('MY_METHODS.DASHBOARD')} element={<Dashboard />} />
            {!isCodeBasedStudent && <Route path={getRoute('MY_METHODS.CATALOGUE')} element={<CataloguePage />} />}
          </Route>

          {showGroupsAndProfile && (
            <Route path={getRoute('MY_PROFILE')} element={<MyProfile />}>
              <Route index element={<Navigate to={getRoute('MY_PROFILE.PERSONAL_DETAILS')} replace />} />
              <Route path={getRoute('MY_PROFILE.PERSONAL_DETAILS')} element={<MyDetails />} />
              <Route path={getRoute('MY_PROFILE.LICENSES')} element={<MyLicenses />} />
            </Route>
          )}

          {showGroupsAndProfile && (
            <Route
              path={getRoute('MY_TEACHERS')}
              element={
                <ProtectedRoute isTeacherAlike={false}>
                  <MyTeachers />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to={getRoute('MY_TEACHERS.CURRENT_SCHOOLYEAR')} replace />} />
              <Route
                path={getRoute('MY_TEACHERS.PREVIOUS_SCHOOLYEAR')}
                element={<MyTeacherTabContent tab="PREVIOUS_SCHOOLYEAR" />}
              />
              <Route
                path={getRoute('MY_TEACHERS.CURRENT_SCHOOLYEAR')}
                element={<MyTeacherTabContent tab="CURRENT_SCHOOLYEAR" />}
              />
            </Route>
          )}

          <Route
            path={getRoute('MY_GROUPS')}
            element={
              <ProtectedRoute isTeacherAlike>
                <MyGroups />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to={getRoute('MY_GROUPS.CURRENT_SCHOOLYEAR')} replace />} />
            <Route
              path={getRoute('MY_GROUPS.PREVIOUS_SCHOOLYEAR')}
              element={
                <ProtectedRoute isTeacherAlike>
                  <GroupsTabContent tab="PREVIOUS_SCHOOLYEAR" />
                </ProtectedRoute>
              }
            />
            <Route
              path={getRoute('MY_GROUPS.CURRENT_SCHOOLYEAR')}
              element={
                <ProtectedRoute isTeacherAlike>
                  <GroupsTabContent tab="CURRENT_SCHOOLYEAR" />
                </ProtectedRoute>
              }
            />
            <Route
              path={getRoute('MY_GROUPS.NEXT_SCHOOLYEAR')}
              element={
                <ProtectedRoute isTeacherAlike>
                  <GroupsTabContent tab="NEXT_SCHOOLYEAR" />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path={getRoute('MATERIAL')} element={withSuspense(MaterialDataProvider)}>
            <Route path={getRoute('MATERIAL.CONTENT')} element={<Outlet />}>
              <Route
                path={getRoute('MATERIAL.VEM')}
                element={
                  <SidebarVisibilityContextProvider>
                    <VirtualExerciseModulePage />
                  </SidebarVisibilityContextProvider>
                }
              >
                <Route index element={<VirtualExerciseModuleOverview />} />
                <Route
                  path={getRoute('MATERIAL.VEM.CATEGORY_DETAIL')}
                  element={<VirtualExerciseModuleCategoryDetail />}
                />
              </Route>
            </Route>

            <Route
              element={
                <SidebarVisibilityContextProvider>
                  <PageLevelLayout />
                </SidebarVisibilityContextProvider>
              }
            >
              {/** submodule selector if applicable or redirect */}
              <Route index element={<Navigate to={getRoute('MATERIAL.CONTENT')} replace />} />
              <Route path={getRoute('MATERIAL.CONTENT')} element={<Outlet />}>
                <Route index element={<SubModuleSelector />} />
                <Route path={getRoute('MATERIAL.CONTENT.TOC')} element={<Toc />} />
              </Route>

              {/** results view for super module */}
              <Route
                path={getRoute('MATERIAL.RESULTS')}
                element={
                  <ProtectedRoute isTeacherAlike>
                    <Results />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to={getRoute('MATERIAL.RESULTS.BY_STUDENT')} replace />} />
                <Route
                  path={getRoute('MATERIAL.RESULTS.BY_STUDENT')}
                  element={
                    <ProtectedRoute isTeacherAlike>
                      <StudentTab />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={getRoute('MATERIAL.RESULTS.BY_ITEM')}
                  element={
                    <ProtectedRoute isTeacherAlike>
                      <ExerciseTab />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                path={getRoute('MATERIAL.ASSIGNMENTS')}
                element={isTeacherAlike ? <TeacherAssignments /> : <StudentAssignments />}
              >
                {isTeacherAlike ? (
                  <>
                    <Route index element={<Navigate to={getRoute('MATERIAL.ASSIGNMENTS.PLANNING')} replace />} />
                    <Route
                      path={getRoute('MATERIAL.ASSIGNMENTS.PLANNING')}
                      element={<TeacherAssignmentTabs tab="PLANNING" />}
                    />
                    <Route
                      path={getRoute('MATERIAL.ASSIGNMENTS.ARCHIVE')}
                      element={<TeacherAssignmentTabs tab="ARCHIVE" />}
                    />
                  </>
                ) : (
                  <>
                    <Route index element={<Navigate to={getRoute('MATERIAL.ASSIGNMENTS.TO_MAKE')} replace />} />
                    <Route
                      path={getRoute('MATERIAL.ASSIGNMENTS.TO_MAKE')}
                      element={<StudentAssignmentTabContent tab="TO_MAKE" />}
                    />
                    <Route
                      path={getRoute('MATERIAL.ASSIGNMENTS.FINISHED')}
                      element={<StudentAssignmentTabContent tab="FINISHED" />}
                    />
                  </>
                )}
              </Route>
              <Route path={getRoute('MATERIAL.SEARCH')} element={<Search />} />
            </Route>
            <Route
              path={getRoute('MATERIAL.CATEGORICAL')}
              element={
                <CategoricalContentTypeContextProvider>
                  <QueryMaterialContextProvider>
                    <SidebarVisibilityContextProvider>
                      <CategoricalMaterialPage />
                    </SidebarVisibilityContextProvider>
                  </QueryMaterialContextProvider>
                </CategoricalContentTypeContextProvider>
              }
            >
              <Route index element={<CategoricalMaterialOverview />} />
              <Route path={getRoute('MATERIAL.CATEGORICAL.BROWSE')} element={<CategoricalMaterialBrowseMode />} />
            </Route>
          </Route>
          <Route path={getRoute('DIRECT_MATERIAL_LINK')} element={<LaunchMaterial />} />
          <Route path={getRoute('LINKED_MATERIAL_LINK')} element={<LinkedMaterialList />} />
          <Route path={getRoute('QR_CODES_LINK')} element={<LaunchQRCode />} />
          <Route path={getRoute('WORDLIST')} element={<StandaloneWordlist />} />
        </Route>
      </Route>
      <Route path="*" element={withSuspense(NotFound)} />
    </Route>
  );
}

export default useRouter;
